import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { cloneDeep } from "lodash-es";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useActionStore } from "../action/actions.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useSimulationStore } from "./simulations.store";
import { useScenarioStore } from "../scenario/scenarios.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type { ExpressionValue } from "../expression/expressionFunctions";

export const useSimulationCreation = () => {
  const simulationView = useSimulationViewStore();
  const scenarioStore = useScenarioStore();
  const settingsStore = useSettingsStore();

  const { t } = useI18n();

  const scenariosSize = computed(() => scenarioStore.length);

  const chosenScenario = computed(() => {
    if (simulationView.newSimulationSettings.scenarioId == null) return null;
    return scenarioStore.items[simulationView.newSimulationSettings.scenarioId];
  });

  const namePlaceholder = computed(() => {
    let name = "";
    if (simulationType.value === "scenario") {
      name += (chosenScenario.value?.title || "") + " ";
    }
    name += t(`labels.${simulationType.value}Simulation`);

    return name;
  });

  const simulationType = computed(() => {
    return (
      simulationView.newSimulationSettings.simulationType ?? defaultSimulationType.value
    );
  });

  const defaultSimulationType = computed(() => {
    if (useActionStore().length > 0 || useIncidentStore().length > 0) {
      return "scenario";
    } else {
      return "impulse";
    }
  });

  const roundAmount = computed(() => {
    return (
      simulationView.newSimulationSettings.roundAmount ??
      settingsStore.simulationRoundAmount
    );
  });

  const maxRounds = computed(() => {
    if (simulationType.value === "impulse") {
      return roundAmount.value;
    }
    return (
      chosenScenario.value?.maxRounds ??
      settingsStore.simulationRoundAmount
    );
  });

  async function createSimulation() {
    const newSimSettings = simulationView.newSimulationSettings;

    const targetStore = useTargetStore();
    const resourceStore = useResourceStore();
    const indexStore = useIndexStore();
    
    const settingsForSimulation = {
      title: newSimSettings.title || namePlaceholder.value,
      description: newSimSettings.description,
      maxRounds: maxRounds.value,
      scenarioId: null as number | null,
      startElementValues: cloneDeep({
        ...targetStore.expressionValues,
        ...resourceStore.expressionValues,
        ...indexStore.expressionValues
      }) as { [key: string]: ExpressionValue },

      actionIds: [] as number[],
      incidentIds: [] as number[],
      useMomentums: true,
      useScheduledEffects: false
    };

    console.log("settings for sim", cloneDeep(settingsForSimulation));

    if (simulationType.value === "impulse") {
      // no scenario
      settingsForSimulation.useMomentums = newSimSettings.momentumsActive;
      settingsForSimulation.useScheduledEffects = true;

      if (newSimSettings.actionsActive) {
        const allActionIds = useActionStore().ids.slice();
        settingsForSimulation.actionIds = allActionIds;
      }
      if (newSimSettings.incidentsActive) {
        const allIncidentIds = useIncidentStore().ids.slice();
        settingsForSimulation.incidentIds = allIncidentIds;
      }
    } else {
      // scenario
      settingsForSimulation.scenarioId = newSimSettings.scenarioId;

      if (chosenScenario.value) {
        for (const targetId in chosenScenario.value.targetValues) {
          const uid = `target_${targetId}`;
          settingsForSimulation.startElementValues[uid].value =
            chosenScenario.value?.targetValues[targetId];
        }
        for (const resourceId in chosenScenario.value.resourceValues) {
          const uid = `resource_${resourceId}`;
          settingsForSimulation.startElementValues[uid].value =
            chosenScenario.value?.resourceValues[resourceId];
        }
      }
    }

    const simulation = await useSimulationStore().initNewSimulation(
      settingsForSimulation
    );

    simulationView.loadSimulation({
      simulationId: simulation.id
    });
  }

  return {
    chosenScenario,
    scenariosSize,
    namePlaceholder,
    simulationType,
    defaultSimulationType,
    maxRounds,
    roundAmount,
    createSimulation
  }
}
